import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "./ServicePages.css";

const seoFeatures = [
  "Technical SEO audits for speed, indexing, and structure",
  "Local neighborhood and city-level keyword targeting",
  "On-page optimization for service pages and listing pages",
  "Content strategy for authority and lead capture",
  "Conversion-focused reporting tied to business outcomes",
  "SEO + website alignment for sustainable long-term growth"
];

const seoPhases = [
  {
    title: "Market & Competitor Discovery",
    detail:
      "We identify search gaps in your city, property niche, and competitor landscape to prioritize high-value opportunities first."
  },
  {
    title: "Technical Foundation",
    detail:
      "We improve crawlability, page speed, indexation health, internal linking, and metadata structure so your site can rank reliably."
  },
  {
    title: "Content & Local Authority",
    detail:
      "We map landing pages, service pages, and local neighborhood content to relevant intent-based keywords and buyer/seller journeys."
  },
  {
    title: "Measurement & Iteration",
    detail:
      "We track ranking movement, lead activity, and page performance, then iterate content and technical priorities monthly."
  }
];

const seoDeliverables = [
  "Keyword and content map by market and intent",
  "Technical SEO audit summary with action priorities",
  "On-page optimization for core conversion pages",
  "Structured local content roadmap",
  "Monthly progress report with ranking and lead insights",
  "Quarterly strategic review and growth recommendations"
];

const seoFaq = [
  {
    q: "How long does SEO take to show results?",
    a:
      "Most markets see measurable improvement in 3 to 6 months, with stronger momentum after foundational fixes and content are in place."
  },
  {
    q: "Do you only work with Apto-built websites?",
    a:
      "No. We can optimize existing sites, but performance is strongest when design, speed, and SEO are aligned across the full Apto stack."
  },
  {
    q: "Will you optimize for specific neighborhoods?",
    a:
      "Yes. Neighborhood and micro-market targeting is central to our real estate SEO approach and content planning."
  },
  {
    q: "How do you measure success?",
    a:
      "We track rankings, organic traffic quality, lead actions, and page-level conversion indicators tied to your business goals."
  }
];

export default function SEOPage() {
  return (
    <>
      <Helmet>
        <title>SEO Services for Real Estate | Apto Websites</title>
        <meta
          name="description"
          content="Real estate SEO services from Apto Websites to improve rankings, increase qualified traffic, and generate stronger leads for agents and brokerages."
        />
      </Helmet>
      <div className="service-page">
        <section className="service-hero">
          <div>
            <p className="service-kicker">SEO services</p>
            <h1>SEO for Real Estate That Turns Rankings into Revenue</h1>
            <p>
              We build search visibility with technical execution, strategic content, and local authority development
              designed for real estate professionals.
            </p>
            <div className="service-quick-actions">
              <Link to="/pricing" className="service-btn primary">View Pricing</Link>
              <Link to="/message" className="service-btn secondary">Talk to an SEO Specialist</Link>
            </div>
          </div>
          <img
            src={require("../../assets/imgs/agentpics/AgentRaise.jpg")}
            alt="Real estate SEO strategy planning"
          />
        </section>

        <section className="service-grid">
          {seoFeatures.map((feature) => (
            <article className="service-card" key={feature}>
              <h3>{feature.split(" ").slice(0, 4).join(" ")}</h3>
              <p>{feature}</p>
            </article>
          ))}
        </section>

        <section className="service-detail-section">
          <header className="service-detail-header">
            <h2>How Our Real Estate SEO Program Works</h2>
            <p>
              Our SEO process is structured for consistency: build a strong technical base, publish intent-focused local
              content, and improve rankings through continuous analysis and iteration.
            </p>
          </header>
          <div className="service-columns">
            {seoPhases.map((phase) => (
              <article className="service-mini-card" key={phase.title}>
                <h3>{phase.title}</h3>
                <p>{phase.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="service-detail-section">
          <div className="service-deliverables">
            <h3>What You Receive</h3>
            <ul>
              {seoDeliverables.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="service-detail-section">
          <header className="service-detail-header">
            <h2>SEO Questions We Often Get</h2>
          </header>
          <div className="service-faq-grid">
            {seoFaq.map((item) => (
              <article className="service-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="service-bottom-cta">
          <h2>Ready to improve search visibility in your market?</h2>
          <p>Let’s align your website, content, and local authority strategy for consistent lead growth.</p>
          <div className="service-quick-actions">
            <Link to="/signup" className="service-btn primary">Get Started</Link>
            <Link to="/pricing" className="service-btn secondary">See Plans</Link>
          </div>
        </section>
      </div>
    </>
  );
}
